import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { cleanEntity } from '../shared/util/entity-utils/entity-utils';
import { createEntitySlice, EntityState, getAllRequest, serializeAxiosError } from '../shared/reducers/reducer.utils';

const apiUrl = 'api/categoria-servicios';

type CategoriaServicioState = EntityState<any> & {
  entitiesEspeciales: any[];
  loadingEspeciales: boolean;
  totalItemsEspeciales: number;
};

const initialState: CategoriaServicioState = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: {},
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  entitiesEspeciales: [],
  loadingEspeciales: false,
  totalItemsEspeciales: 0,
};

async function fetchCategoriaServiciosList(request: any) {
  const { proveedorId, ...rest } = request ?? {};
  const params = getAllRequest(rest);
  if (proveedorId != null && proveedorId !== '') {
    return axios.get<any[]>(
      `api/proveedors/${proveedorId}/categoria-servicios?${params}&cacheBuster=${new Date().getTime()}`,
    );
  }
  return axios.get<any[]>(`${apiUrl}?${params}&cacheBuster=${new Date().getTime()}`);
}

export const getEntities = createAsyncThunk(
  'categoriaServicio/fetch_entity_list',
  async (request: any) => fetchCategoriaServiciosList(request),
  { serializeError: serializeAxiosError },
);

export const getEntitiesEspeciales = createAsyncThunk(
  'categoriaServicio/fetch_entity_list_especiales',
  async (request: any) => {
    const filters = [
      ...(request?.filters ?? []),
      { filterName: 'especial', filterValue: 'true' },
    ];
    return fetchCategoriaServiciosList({ ...request, filters });
  },
  { serializeError: serializeAxiosError },
);

export const getEntity = createAsyncThunk(
  'categoriaServicio/fetch_entity',
  async (id: string | number) => axios.get<any>(`${apiUrl}/${id}`),
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'categoriaServicio/create_entity',
  async (entity: any) => axios.post<any>(apiUrl, cleanEntity(entity)),
  { serializeError: serializeAxiosError },
);

export const createEntityForProveedor = createAsyncThunk(
  'categoriaServicio/create_entity_for_proveedor',
  async ({ proveedorId, entity }: { proveedorId: number; entity: Record<string, unknown> }) =>
    axios.post<any>(
      `api/proveedors/${proveedorId}/categoria-servicios`,
      cleanEntity(entity),
    ),
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'categoriaServicio/update_entity',
  async (entity: any) => axios.put<any>(`${apiUrl}/${entity.id}`, cleanEntity(entity)),
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'categoriaServicio/delete_entity',
  async (id: string | number) => axios.delete<any>(`${apiUrl}/${id}`),
  { serializeError: serializeAxiosError },
);

export const CategoriaServicioSlice = createEntitySlice({
  name: 'categoriaServicio',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;
        return {
          ...state,
          loading: false,
          entities: data,
          totalItems: parseInt(headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getEntitiesEspeciales), (state, action) => {
        const { data, headers } = action.payload;
        const s = state as unknown as CategoriaServicioState;
        s.loadingEspeciales = false;
        s.entitiesEspeciales = data;
        s.totalItemsEspeciales = parseInt(headers['x-total-count'], 10);
      })
      .addMatcher(isFulfilled(createEntity, createEntityForProveedor, updateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(getEntitiesEspeciales), state => {
        state.errorMessage = null;
        (state as unknown as CategoriaServicioState).loadingEspeciales = true;
      })
      .addMatcher(isPending(getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, createEntityForProveedor, updateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = CategoriaServicioSlice.actions;
export default CategoriaServicioSlice.reducer;
